import {
  DateString,
  TimeString,
  convert24HourToTimestampMs,
} from './open-now.utils';

export interface ClosedTodayInputs {
  closedToday: boolean;
  closingTodayAt?: string;
  timeZoneOffSet: string;
}

/**
 * In backend, if a closure is scheduled (but the location is still open),
 * the `closedToday` flag is set immediately, along with `closingTodayAt`.
 *
 * This function converts `closedToday` to meet the UI assumptions:
 * - Lab currently open with no upcoming closure: `closedToday` is `false`
 * - Lab currently open with upcoming closure: `closedToday` is `false`, `closingTodayAt` is set
 * - Lab has passed the closure time: `closedToday` is `true`, `closingTodayAt` is ignored
 * - Lab is closed, had no specific time specified: `closedToday` is `true`, `closingTodayAt` is ignored
 *
 * @param inputs Inputs to derive one to two (if lunch hours set) sets of open hours.
 * @param today Today's date in `yyyy-MM-dd` format, set as an input for unit testability.
 * @param nowMs "now", in milliseconds, from `Date.now()`, but is set as an input for unit testability.
 */
export const isClosedToday = (
  inputs: ClosedTodayInputs,
  today: DateString,
  nowMs: number
): boolean => {
  const { timeZoneOffSet, closedToday, closingTodayAt } = inputs;
  // If any of the fields aren't set pass through `closedToday`
  if (!closedToday || !closingTodayAt) {
    return closedToday;
  }

  return (
    nowMs >=
    convert24HourToTimestampMs(
      today,
      closingTodayAt as TimeString,
      timeZoneOffSet
    )
  );
};
